// client/src/components/Insights/shared/SharedComponents.jsx
import React from 'react';
import { Card, CardContent, Box, Typography, Grid, Skeleton, useTheme } from '@mui/material';
import { GRADIENT, alpha, gradientTextSx } from './constants';

export const useGlass = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return {
    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(20px)',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
    borderRadius: 3,
  };
};

export const GlassCard = ({ children, sx, ...props }) => {
  const glass = useGlass();
  return <Card sx={{ ...glass, ...sx }} {...props}>{children}</Card>;
};

export const StatCard = ({ label, value, icon: Icon, color = '#8b5cf6', sub, gradient }) => (
  <GlassCard sx={{ flex: 1, minWidth: 140, borderLeft: `3px solid ${color}` }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {Icon && (
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: gradient || alpha(color, 0.12) }}>
            <Icon size={14} color={gradient ? '#fff' : color} />
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>{label}</Typography>
      </Box>
      <Typography variant="h6" fontWeight={800} sx={gradient ? gradientTextSx : { color }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>{sub}</Typography>}
    </CardContent>
  </GlassCard>
);

export const LoadingGrid = ({ n = 4, height = 100 }) => (
  <Grid container spacing={2}>
    {Array.from({ length: n }).map((_, i) => (
      <Grid item xs={12} sm={6} md={12 / Math.min(n, 4)} key={i}>
        <Skeleton variant="rounded" height={height} sx={{ borderRadius: 3 }} />
      </Grid>
    ))}
  </Grid>
);

export const EmptyState = ({ icon: Icon, title, subtitle }) => (
  <Box sx={{ textAlign: 'center', py: 8, opacity: 0.7 }}>
    <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: alpha('#8b5cf6', 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
      <Icon size={36} color="#8b5cf6" />
    </Box>
    <Typography variant="h6" fontWeight={700} gutterBottom>{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>{subtitle}</Typography>
  </Box>
);

export const SectionTitle = ({ icon: Icon, children, action }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {Icon && <Box sx={{ width: 32, height: 32, borderRadius: 2, background: GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={16} color="#fff" /></Box>}
      <Typography variant="h6" fontWeight={800} sx={gradientTextSx}>{children}</Typography>
    </Box>
    {action}
  </Box>
);

export const TypingDots = () => (
  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', p: 1 }}>
    {[0, 1, 2].map(i => (
      <Box key={i} sx={{
        width: 6, height: 6, borderRadius: '50%', background: '#8b5cf6',
        animation: 'typingBounce 1.4s infinite ease-in-out',
        animationDelay: `${i * 0.16}s`,
        '@keyframes typingBounce': {
          '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
          '40%': { transform: 'scale(1)', opacity: 1 },
        },
      }} />
    ))}
  </Box>
);

export const ScoreRing = ({ score = 0, size = 160, strokeWidth = 10, label }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(score, 100) / 100) * circumference;
  const color = score >= 80 ? '#10b981' : score >= 60 ? '#3b82f6' : score >= 40 ? '#f59e0b' : '#ef4444';
  const grade = score >= 90 ? 'A+' : score >= 80 ? 'A' : score >= 70 ? 'B' : score >= 60 ? 'C' : score >= 50 ? 'D' : 'F';

  return (
    <Box sx={{ position: 'relative', width: size, height: size, mx: 'auto' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={alpha('#9ca3af', 0.15)} strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.5s cubic-bezier(0.4,0,0.2,1)' }} />
      </svg>
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
        <Typography variant="h3" fontWeight={900} sx={{ color, lineHeight: 1 }}>{score}</Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>{grade}</Typography>
        {label && <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: 9, mt: 0.5 }}>{label}</Typography>}
      </Box>
    </Box>
  );
};
